import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AuthContextType {
  isAuthenticated: boolean
  email: string | null
  token: string | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const AUTH_TOKEN_KEY = 'bollettaamica_admin_token'
const AUTH_EMAIL_KEY = 'bollettaamica_admin_email'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY)
    const storedEmail = localStorage.getItem(AUTH_EMAIL_KEY)

    if (storedToken && storedEmail) {
      // Verify token is still valid
      verifyToken(storedToken).then(valid => {
        if (valid) {
          setToken(storedToken)
          setEmail(storedEmail)
        } else {
          localStorage.removeItem(AUTH_TOKEN_KEY)
          localStorage.removeItem(AUTH_EMAIL_KEY)
        }
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [])

  const verifyToken = async (tokenToVerify: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/admin/verify', {
        headers: {
          Authorization: `Bearer ${tokenToVerify}`,
        },
      })
      const data = await response.json()
      return data.valid === true
    } catch {
      return false
    }
  }

  const login = async (emailInput: string, password: string) => {
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailInput, password }),
      })

      const data = await response.json()

      if (response.ok && data.token) {
        setToken(data.token)
        setEmail(data.email)
        localStorage.setItem(AUTH_TOKEN_KEY, data.token)
        localStorage.setItem(AUTH_EMAIL_KEY, data.email)
        return { success: true }
      }

      return { success: false, error: data.error || 'Login failed' }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const logout = async () => {
    if (token) {
      try {
        await fetch('/api/admin/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      } catch {
        // Ignore errors on logout
      }
    }

    setToken(null)
    setEmail(null)
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_EMAIL_KEY)
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        email,
        token,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Protected Route component
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F8B120]"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    window.location.href = '/admin/login'
    return null
  }

  return <>{children}</>
}
